import React, { useState, useContext, useEffect } from 'react';
import {
    Toolbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Grid,
    Fab,
    Typography,
    Button,
    TextField,
    MenuItem,
} from '@material-ui/core';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@material-ui/icons/FilterList';
import EventIcon from '@material-ui/icons/Event';
import { makeStyles, styled } from '@material-ui/core/styles';
import { grey } from '@material-ui/core/colors';

import SideMenu from '../../components/SideMenu';
import InputSearch from '../../components/InputSearch';
import Alert from '../../components/Alert';
import EvidenceMediaButton from '../../components/EvidenceMediaButton';
import Popup from '../../components/Popup';

import api from '../../services/api';
import date from '../../utils/date';
import { PermissionsContext } from '../../providers/Permissions';
import { ListPageHeading, ListTableCard, listResultSubtitle } from '../../components/ListPageLayout';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default function CaseFileList() {
    const classes = useStyles();

    const {
        PermissionWrapper,
        PermissionOneOfWrapper,
    } = useContext(PermissionsContext);

    const [casesFiles, setCasesFiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [refresh, setRefresh] = useState(true)
    const [limit, setLimit] = useState(10);
    const [openDeletePopup, setOpenDeletePopup] = useState(false);
    const [openUploadFile, setOpenUploadFile] = useState(false);
    const [file, setFile] = useState(null);
    const [source, setSource] = useState('upload');
    const [eventKind, setEventKind] = useState('person');
    const [personName, setPersonName] = useState('');
    const [cameraName, setCameraName] = useState('');
    const [personType, setPersonType] = useState('');
    const [personTypes, setPersonTypes] = useState([]);
    const [events, setEvents] = useState([]);
    const [eventCount, setEventCount] = useState(0);
    const [eventPage, setEventPage] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState('');
    const [cases, setCases] = useState([]);
    const [caseId, setCaseId] = useState('');
    const [caseSearch, setCaseSearch] = useState('');
    const [eventStart, setEventStart] = useState('');
    const [eventEnd, setEventEnd] = useState('');
    const [savingEvent, setSavingEvent] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletingCaseFileId, setDeletingCaseFileId] = useState(null);

    const [message, setMessage] = useState({
        open: false,
        variant: '',
        text: '',
    });

    const handleOpenUpload = () => {
        setOpenUploadFile(true);
        findCases('');
        api.get('/person_type/list', { params: { limit: 99, page: 0, show_exceptions: true } })
            .then(({ data }) => setPersonTypes(data.person_types || data.personTypes || []))
            .catch(() => setPersonTypes([]));
    };

    const findCases = async (search) => {
        try {
            const { data } = await api.get('/case/list', { params: { search, limit: 30, page: 0 } });
            setCases(data.cases || []);
        } catch (err) {
            setCases([]);
        }
    };

    const findEvents = async (requestedPage = 0) => {
        try {
            const { data } = await api.get('/case_file/events', { params: {
                kind: eventKind, name: eventKind === 'person' ? personName : '',
                camera: cameraName,
                person_type: eventKind === 'person' ? personType : '',
                start: eventStart || undefined, end: eventEnd || undefined, page: requestedPage,
            } });
            setEvents(data.events || []);
            setEventCount(data.count || 0);
            setEventPage(requestedPage);
            setSelectedEvent('');
        } catch (err) {
            setMessage({ open: true, variant: 'error', text: err.error || 'Não foi possível buscar eventos' });
        }
    };

    useEffect(() => {
        if (openUploadFile && source === 'event') findEvents(0);
        // Os demais filtros são aplicados ao clicar em "Buscar eventos".
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openUploadFile, source, eventKind]);

    const saveEventEvidence = async () => {
        if (!selectedEvent) return;
        try {
            setSavingEvent(true);
            await api.post('/case_file/create_from_event', {
                event_id: Number(selectedEvent), case_id: caseId ? Number(caseId) : null,
            });
            setOpenUploadFile(false);
            setRefresh(value => !value);
            setMessage({ open: true, variant: 'success', text: 'Evidência criada com sucesso' });
        } catch (err) {
            setMessage({ open: true, variant: 'error', text: err.error || 'Não foi possível criar a evidência' });
        } finally {
            setSavingEvent(false);
        }
    };

    const handleCloseUpload = () => {
        setOpenUploadFile(false);
    };

    const closeMessage = () => {
        setMessage({
            open: false,
            variant: '',
            text: '',
        });
    };

    const handleDelete = (id) => {
        setDeletingCaseFileId(id);
        setOpenDeletePopup(true);
    }

    async function fetchCasesFiles({ search }) {
        const loadData = async () => {
            try {
                const result = await api.get('/case_file/list', {
                    params: {
                        search,
                        limit,
                        page,
                    },
                });

                const { count, cases_files } = result.data;

                setCasesFiles(cases_files);
                setTotal(count);
            }
            catch (err) {
                console.log(err);
            }
        };

        loadData();
    }

    const changePage = (_, newPage) => {
        setPage(newPage);
    };

    const quantityPerPage = ({ target }) => {
        setLimit(parseInt(target.value, 10));
        setPage(0);
    };

    const deleteCaseFile = async () => {
        try {
            setIsDeleting(true);

            const result = await api.post(`/case_file/delete/${deletingCaseFileId}`);

            const { success } = result.data;

            setIsDeleting(false);

            if (success) {
                setDeletingCaseFileId(null);
                setOpenDeletePopup(false);
                setRefresh(!refresh);
                setMessage({
                    open: true,
                    variant: 'success',
                    text: `Evidência deletada com sucesso!`,
                });
            }
        }
        catch (err) {
            setIsDeleting(false);
            setMessage({
                open: true,
                variant: 'error',
                text: err.error,
            });
        }
    }

    const saveCaseFile = async () => {
        try {
            const formData = new FormData();

            formData.append('case_file', file);

            const result = await api.post('/case_file/create', formData);

            const { name } = result.data;

            await fetchCasesFiles({ search: '' });
            setFile(null);

            setMessage({
                open: true,
                variant: 'success',
                text: `Evidência: ${name} cadastrado com sucesso!`,
            });
        }
        catch (err) {
            setMessage({
                open: true,
                variant: 'error',
                text: err.error,
            });
        }
    };

    return (
        <div className={classes.root}>
            <SideMenu />
            <main className={classes.content}>
                <Toolbar />
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <ListPageHeading icon="caseFile" title="Lista de Evidências" />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <ListTableCard
                            title="Evidências cadastradas"
                            subtitle={listResultSubtitle(total)}
                            actions={(
<InputSearch
                            textFieldProps={{
                                placeholder: 'Buscar…',
                            }}
                            request={fetchCasesFiles}
                            pagination={[page, limit, refresh]}
                        />
                            )}
                        >
                            <TableContainer component={Paper}>
                            <Table className={classes.table} size="small" aria-label="access history">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableHead}>ID</TableCell>
                                        <TableCell className={classes.tableHead}>Arquivo</TableCell>
                                        <TableCell className={classes.tableHead}>Nome</TableCell>
                                        <TableCell className={classes.tableHead}>Caso</TableCell>
                                        <TableCell className={classes.tableHead}>Responsável</TableCell>
                                        <TableCell className={classes.tableHead}>Data de criação</TableCell>
                                        <PermissionOneOfWrapper
                                            routes={[
                                                "/evidencias/deletar/:id",
                                            ]}
                                        >
                                            <TableCell className={classes.tableHead}>Ações</TableCell>
                                        </PermissionOneOfWrapper>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!!casesFiles && casesFiles.map(caseFile => (
                                        <TableRow key={caseFile.id}>
                                            <TableCell className={classes.tableBody} component="th" scope="row">
                                                {caseFile.id}
                                            </TableCell>
                                            <TableCell className={classes.tableBody}>
                                                {!caseFile.removed ? (
                                                    <EvidenceMediaButton file={caseFile} />
                                                ) : (
                                                    <Typography variant="subtitle2">REMOVIDO</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell className={classes.tableBody}>
                                                {caseFile.name}
                                            </TableCell>
                                            <TableCell className={classes.tableBody}>
                                                {caseFile?.case?.name}
                                            </TableCell>
                                            <TableCell className={classes.tableBody}>
                                                {caseFile.user.name}
                                            </TableCell>
                                            <TableCell className={classes.tableBody}>
                                                {date(caseFile.created_at).format('DD/MM/YYYY HH:mm')}
                                            </TableCell>
                                            <PermissionOneOfWrapper
                                                routes={[
                                                    "/evidencias/deletar/:id",
                                                ]}
                                            >
                                                <TableCell className={classes.tableBody}>
                                                    <PermissionWrapper route="/evidencias/deletar/:id">
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            size="small"
                                                            color="secondary"
                                                            className={classes.actionButton}
                                                            onClick={() => handleDelete(caseFile.id)}
                                                        >
                                                            <DeleteIcon style={{ fontSize: 20 }} />
                                                        </Button>
                                                    </PermissionWrapper>
                                                </TableCell>
                                            </PermissionOneOfWrapper>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </ListTableCard>
                        <TablePagination
                            rowsPerPageOptions={[10, 20, 30]}
                            component="div"
                            count={total}
                            rowsPerPage={limit}
                            page={page}
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                            labelRowsPerPage="Registros por página"
                            onChangePage={changePage}
                            onChangeRowsPerPage={quantityPerPage}
                        />
                    </Grid>
                    <Popup
                        openPopup={openDeletePopup}
                        setOpenPopup={setOpenDeletePopup}

                    >
                        <Grid container xs={12} style={{
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 3% 2% 3%',

                        }}>
                            <Typography variant='h5'
                                style={{
                                    fontWeight: 'bold',
                                    color: '#9e9e9e'
                                }}
                            >
                                Deseja mesmo deletar esta evidência?
                            </Typography>
                            <Grid item style={{
                                marginTop: '50px'
                            }}>
                                <Button
                                    disabled={isDeleting}
                                    variant="contained"
                                    onClick={() => deleteCaseFile()}
                                    color="secondary"
                                    endIcon={<DeleteIcon />}
                                >
                                    {isDeleting ? 'Deletando...' : 'Sim, delete esta evidência'}
                                </Button>
                            </Grid>

                        </Grid>
                    </Popup>
                </Grid>
                <Dialog open={openUploadFile} onClose={handleCloseUpload} maxWidth="md" fullWidth>
                    <DialogTitle>Nova evidência</DialogTitle>
                    <DialogContent className={classes.evidenceModal}>
                        <div className={classes.modalIntro}>
                            <div className={classes.modalIntroIcon}><EventIcon /></div>
                            <div>
                                <Typography className={classes.modalIntroTitle}>Escolha a origem da evidência</Typography>
                                <Typography variant="body2">Envie um arquivo ou selecione um evento do IDCloud.</Typography>
                            </div>
                        </div>
                        <Paper variant="outlined" className={classes.modalSection}>
                            <TextField select fullWidth variant="outlined" size="small" label="Origem da evidência" value={source}
                                onChange={event => setSource(event.target.value)}>
                                <MenuItem value="upload">Enviar arquivo</MenuItem>
                                <MenuItem value="event">Evento do IDCloud</MenuItem>
                            </TextField>
                        </Paper>
                        {source === 'event' ? <>
                            <Paper variant="outlined" className={classes.modalSection}>
                                <div className={classes.sectionHeading}><FilterListIcon color="primary" />
                                    <div><Typography variant="subtitle1"><strong>Filtrar eventos</strong></Typography>
                                        <Typography variant="body2" color="textSecondary">Refine a busca para encontrar o evento desejado.</Typography></div>
                                </div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}><TextField select fullWidth variant="outlined" size="small" label="Tipo de evento" value={eventKind}
                                        onChange={event => { setEventKind(event.target.value); setEvents([]); setSelectedEvent(''); }}>
                                        <MenuItem value="person">Pessoa</MenuItem><MenuItem value="vehicle">Veículo</MenuItem>
                                    </TextField></Grid>
                                    {eventKind === 'person' && <>
                                        <Grid item xs={12} sm={6}><TextField fullWidth variant="outlined" size="small" label="Nome da pessoa" value={personName}
                                            onChange={event => setPersonName(event.target.value)} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField select fullWidth variant="outlined" size="small" label="Tipo de pessoa" value={personType}
                                            onChange={event => setPersonType(event.target.value)}>
                                            <MenuItem value="">Todos</MenuItem>
                                            {personTypes.map(type => <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>)}
                                        </TextField></Grid>
                                    </>}
                                    <Grid item xs={12} sm={6}><TextField fullWidth variant="outlined" size="small" label="Nome da câmera" value={cameraName}
                                        onChange={event => setCameraName(event.target.value)} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth variant="outlined" size="small" type="datetime-local" label="De"
                                        InputLabelProps={{ shrink: true }} value={eventStart} onChange={event => setEventStart(event.target.value)} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth variant="outlined" size="small" type="datetime-local" label="Até"
                                        InputLabelProps={{ shrink: true }} value={eventEnd} onChange={event => setEventEnd(event.target.value)} /></Grid>
                                </Grid>
                                <Button className={classes.searchButton} color="primary" variant="contained" onClick={() => findEvents(0)}>Buscar eventos</Button>
                            </Paper>
                            <Paper variant="outlined" className={classes.modalSection}>
                                <Typography variant="subtitle1"><strong>Selecione um evento</strong></Typography>
                                <Typography variant="body2" color="textSecondary">{eventCount} eventos encontrados</Typography>
                                <div className={classes.eventResults}>
                                    {events.map(event => <label key={event.id} className={classes.eventRow}>
                                        <input type="radio" name="selectedEvent" value={event.id} checked={String(selectedEvent) === String(event.id)}
                                            disabled={!event.has_media} onChange={() => setSelectedEvent(event.id)} />
                                        <span><strong>{event.user?.name || event.camera?.name || event.device?.name || 'Sem identificação'}</strong><br />
                                            <small>#{event.id} · {date(event.event_time).format('DD/MM/YYYY HH:mm')}{event.has_media ? '' : ' · Sem mídia'}</small></span>
                                    </label>)}
                                </div>
                                <div className={classes.paginationActions}>
                                    <Button disabled={eventPage === 0} onClick={() => findEvents(eventPage - 1)}>Anterior</Button>
                                    <Button disabled={(eventPage + 1) * 10 >= eventCount} onClick={() => findEvents(eventPage + 1)}>Próxima</Button>
                                </div>
                            </Paper>
                            <Paper variant="outlined" className={classes.modalSection}>
                                <Typography variant="subtitle1"><strong>Vincular a um caso</strong></Typography>
                                <Typography variant="body2" color="textSecondary">Esta etapa é opcional.</Typography>
                                <Grid container spacing={2} className={classes.caseFields}>
                                    <Grid item xs={12} sm={7}><TextField select fullWidth variant="outlined" size="small" label="Adicionar ao caso" value={caseId}
                                        onChange={event => setCaseId(event.target.value)}>
                                        <MenuItem value="">Sem caso por enquanto</MenuItem>
                                        {cases.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </TextField></Grid>
                                    <Grid item xs={12} sm={5}><TextField fullWidth variant="outlined" size="small" label="Buscar caso pelo nome" value={caseSearch}
                                        onChange={event => setCaseSearch(event.target.value)} /></Grid>
                                </Grid>
                                <Button onClick={() => findCases(caseSearch)}>Buscar casos</Button>
                            </Paper>
                        </> : <Paper variant="outlined" className={classes.modalSection}>
                        <Button
                            component="label"
                            variant="contained"
                            color="primary"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            style={{ height: 60 }}
                        >
                            Upload de evidência
                            <VisuallyHiddenInput type="file" onChange={event => {
                                const file = event.currentTarget.files[0];

                                setFile(file);
                            }} />
                        </Button>
                        <Typography variant="subtitle2" style={{ marginTop: 20 }}>
                            {!!file && `ARQUIVO: ${file.name}`}
                        </Typography>
                        </Paper>}
                    </DialogContent>
                    <DialogActions>
                        <Button color="secondary" onClick={handleCloseUpload}>Cancelar</Button>
                        <Button color="primary" disabled={source === 'event' && (!selectedEvent || savingEvent)}
                            onClick={source === 'event' ? saveEventEvidence : saveCaseFile}>
                            {savingEvent ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>
                <PermissionWrapper route="/evidencias/criar">
                    <Fab
                        color="primary"
                        aria-label="add"
                        className={classes.fabButton}
                        onClick={handleOpenUpload}
                    >
                        <AddIcon />
                    </Fab>
                </PermissionWrapper>
                {!!message.open && (
                    <Alert
                        open={message.open}
                        autoHideDuration={6000}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        severity={message.variant}
                        onClose={closeMessage}
                    >
                        {message.text}
                    </Alert>
                )}

            </main>
        </div>
    );
}

const useStyles = makeStyles((theme) => ({
    evidenceModal: { paddingTop: theme.spacing(1) },
    modalIntro: {
        display: 'flex', alignItems: 'center', gap: theme.spacing(2),
        padding: theme.spacing(2), marginBottom: theme.spacing(2),
        borderRadius: 14, border: '1px solid #dbeafe',
        background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
        color: '#1f2937',
    },
    modalIntroIcon: {
        display: 'flex', padding: theme.spacing(1.2), borderRadius: 11,
        background: '#1976d2', color: '#fff',
    },
    modalIntroTitle: { fontWeight: 700 },
    modalSection: {
        padding: theme.spacing(2), marginBottom: theme.spacing(2),
        borderRadius: 14, borderColor: '#e5eaf1',
    },
    sectionHeading: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) },
    searchButton: { marginTop: theme.spacing(2), textTransform: 'none', borderRadius: 9 },
    eventResults: { maxHeight: 240, overflowY: 'auto', marginTop: theme.spacing(1) },
    eventRow: {
        display: 'flex', alignItems: 'center', gap: theme.spacing(1.5),
        padding: theme.spacing(1.5), borderBottom: '1px solid #eef1f5',
        cursor: 'pointer', color: '#374151',
    },
    paginationActions: { display: 'flex', justifyContent: 'space-between', marginTop: theme.spacing(1) },
    caseFields: { marginTop: theme.spacing(1) },
    root: {
        display: 'flex',
    },
    content: {
        flexGrow: 1,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(6),
        marginTop: theme.spacing(5),
    },
    title: {
        color: grey[700],
        fontSize: 28,
    },
    table: {
        minWidth: 500,
        width: '100%',
    },
    tableHead: {
        textAlign: 'center',
        textTransform: 'uppercase',
        color: grey[500],
    },
    tableBody: {
        color: '#374151',
        padding: '14px 10px',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 600,
        borderBottom: '1px solid #eef1f5',
    },
    actionButton: {
        margin: theme.spacing(0, .5),
        padding: theme.spacing(.5, 0),
        minWidth: 30,
        borderRadius: 9,
    },
    fabButton: {
        position: 'fixed',
        bottom: 0,
        right: 0,
        margin: theme.spacing(3),
    },
}));
